import api from './api';

export type S3CredentialSource = 'static' | 'vault' | 'environment';

export interface S3BucketConfig {
  id: string;
  name: string;
  endpoint: string;
  region: string;
  bucket: string;

  // Credential configuration
  credentialSource: S3CredentialSource;

  // Static credentials (when credentialSource === 'static')
  accessKeyId?: string;
  secretAccessKey?: string;
  sessionToken?: string;

  // Vault credentials (when credentialSource === 'vault')
  vaultPathId?: string; // Reference to VaultAWSPath.id from integrations

  // Environment credentials (when credentialSource === 'environment')
  // Uses AWS_ACCESS_KEY_ID, AWS_SECRET_ACCESS_KEY from environment

  isDefault?: boolean;
  autoDiscoverEnvironments?: boolean; // Whether to auto-discover environments from bucket (defaults to true)
  description?: string;
  createdAt: Date;
}

export interface S3BucketEnvironment {
  id: string;
  bucketId: string;
  name: string; // Display name
  prefix: string; // S3 prefix/path
  description?: string;
  createdAt: Date;
}

export interface CMI5Package {
  key: string;
  name: string;
  size: number;
  lastModified: string;
  etag?: string;
  metadata?: {
    activityName?: string;
    activityId?: string;
    tenantId?: string;
    courseId?: string;
  };
  versionInfo?: {
    currentVersion: string;
    totalVersions: number;
    hasVersionHistory: boolean;
  };
}

export const s3BucketsApi = {
  // ============================================================================
  // Bucket Management
  // ============================================================================
  
  async listBuckets(): Promise<S3BucketConfig[]> {
    const response = await api.get('/s3-buckets');
    return response.data;
  },

  async addBucket(config: Omit<S3BucketConfig, 'id' | 'createdAt'>): Promise<S3BucketConfig> {
    const response = await api.post('/s3-buckets', config);
    return response.data;
  },

  async updateBucket(id: string, config: Partial<Omit<S3BucketConfig, 'id' | 'createdAt'>>): Promise<S3BucketConfig> {
    const response = await api.put(`/s3-buckets/${id}`, config);
    return response.data;
  },

  async removeBucket(id: string, deleteContent: boolean = false): Promise<void> {
    await api.delete(`/s3-buckets/${id}?deleteContent=${deleteContent}`);
  },

  // ============================================================================
  // Environment Management
  // ============================================================================

  async listEnvironments(): Promise<S3BucketEnvironment[]> {
    const response = await api.get('/s3-environments');
    return response.data;
  },

  async listEnvironmentsByBucket(bucketId: string): Promise<S3BucketEnvironment[]> {
    const response = await api.get(`/s3-buckets/${bucketId}/environments`);
    return response.data;
  },

  async addEnvironment(env: Omit<S3BucketEnvironment, 'id' | 'createdAt'>): Promise<S3BucketEnvironment> {
    const response = await api.post('/s3-environments', env);
    return response.data;
  },

  async updateEnvironment(id: string, updates: Partial<Omit<S3BucketEnvironment, 'id' | 'createdAt'>>): Promise<S3BucketEnvironment> {
    const response = await api.put(`/s3-environments/${id}`, updates);
    return response.data;
  },

  async removeEnvironment(id: string, deleteContent: boolean = false): Promise<void> {
    await api.delete(`/s3-environments/${id}?deleteContent=${deleteContent}`);
  },

  // ============================================================================
  // CMI5 Package Operations
  // ============================================================================

  async listCMI5Packages(bucketId: string, envId: string): Promise<CMI5Package[]> {
    const response = await api.get(`/s3-buckets/${bucketId}/environments/${envId}/cmi5-packages`);
    return response.data;
  },

  async getRC5Config(bucketId: string, envId: string, packageKey: string): Promise<any> {
    const response = await api.get(`/s3-buckets/${bucketId}/environments/${envId}/cmi5-packages/${encodeURIComponent(packageKey)}/rc5`);
    return response.data;
  },

  async getCfgJson(bucketId: string, envId: string, packageKey: string): Promise<any> {
    const response = await api.get(`/s3-buckets/${bucketId}/environments/${envId}/cmi5-packages/${encodeURIComponent(packageKey)}/cfg`);
    return response.data;
  },

  async getObjectMetadata(bucketId: string, envId: string, objectKey: string): Promise<Record<string, string>> {
    const response = await api.get(`/s3-buckets/${bucketId}/environments/${envId}/object-metadata/${encodeURIComponent(objectKey)}`);
    return response.data;
  },

  async updateCfgJson(bucketId: string, envId: string, packageKey: string, cfgData: any): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`/s3-buckets/${bucketId}/environments/${envId}/cmi5-packages/${encodeURIComponent(packageKey)}/cfg`, cfgData);
    return response.data;
  },

  async uploadPackage(bucketId: string, envId: string, packageKey: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/s3-buckets/${bucketId}/environments/${envId}/cmi5-packages/${encodeURIComponent(packageKey)}/upload`);
    return response.data;
  },

  // ============================================================================
  // Scenario Definition Management
  // ============================================================================

  async saveScenarioDefinition(bucketId: string, envId: string, packageKey: string, auId: string, definition: any): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/s3-buckets/${bucketId}/environments/${envId}/cmi5-packages/${encodeURIComponent(packageKey)}/scenarios/${encodeURIComponent(auId)}`, definition);
    return response.data;
  },

  async getScenarioDefinition(bucketId: string, envId: string, packageKey: string, auId: string): Promise<any> {
    const response = await api.get(`/s3-buckets/${bucketId}/environments/${envId}/cmi5-packages/${encodeURIComponent(packageKey)}/scenarios/${encodeURIComponent(auId)}`);
    return response.data;
  },

  async listScenarioDefinitions(bucketId: string, envId: string, packageKey: string): Promise<Array<{ auId: string; definition: any }>> {
    const response = await api.get(`/s3-buckets/${bucketId}/environments/${envId}/cmi5-packages/${encodeURIComponent(packageKey)}/scenarios`);
    return response.data;
  },

  async checkScenarioDraftAvailability(bucketId: string, envId: string, packageKey: string, rangeosApiUrl: string, scenarioUuid: string): Promise<{ hasDraft: boolean; scenarioName?: string; draftId?: string }> {
    const response = await api.post(`/s3-buckets/${bucketId}/environments/${envId}/cmi5-packages/${encodeURIComponent(packageKey)}/check-scenario-draft`, {
      rangeosApiUrl,
      scenarioUuid,
    });
    return response.data;
  },

  async exportScenarioFromRangeOS(bucketId: string, envId: string, packageKey: string, rangeosApiUrl: string, designId: string, auId: string): Promise<{ success: boolean; message: string; auId: string; designId: string }> {
    const response = await api.post(`/s3-buckets/${bucketId}/environments/${envId}/cmi5-packages/${encodeURIComponent(packageKey)}/export-scenario`, {
      rangeosApiUrl,
      // bearerToken is no longer needed - server uses user's Keycloak token from Authorization header
      designId,
      auId,
    });
    return response.data;
  },

  async importScenarioToRangeOS(bucketId: string, envId: string, packageKey: string, rangeosApiUrl: string, auId: string, scenarioUuid?: string): Promise<{ success: boolean; message: string; draftId?: string; auId: string }> {
    const response = await api.post(`/s3-buckets/${bucketId}/environments/${envId}/cmi5-packages/${encodeURIComponent(packageKey)}/import-scenario`, {
      rangeosApiUrl,
      // bearerToken is no longer needed - server uses user's Keycloak token from Authorization header
      auId,
      scenarioUuid,  // Pass the scenario UUID to load the correct definition
    });
    return response.data;
  },

  async importPackage(bucketId: string, envId: string, file: File, metadata?: { activityName?: string; activityId?: string; tenantId?: string; courseId?: string }): Promise<{ success: boolean; s3Key: string; message: string }> {
    const formData = new FormData();
    formData.append('package', file);
    if (metadata?.activityName) formData.append('activityName', metadata.activityName);
    if (metadata?.activityId) formData.append('activityId', metadata.activityId);
    if (metadata?.tenantId) formData.append('tenantId', metadata.tenantId);
    if (metadata?.courseId) formData.append('courseId', metadata.courseId);

    const response = await api.post(`/s3-buckets/${bucketId}/environments/${envId}/cmi5-packages/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // ============================================================================
  // Draft Scenario Management
  // ============================================================================

  async listDraftScenarios(bucketId: string, envId: string): Promise<Array<{
    uuid: string;
    name: string;
    activityId?: number;
    fileName: string;
    draft: any;
  }>> {
    const response = await api.get(`/s3-buckets/${bucketId}/environments/${envId}/draft-scenarios`);
    return response.data;
  },

  // ============================================================================
  // General File Operations
  // ============================================================================

  async listFiles(environmentPrefix: string, subfolder: string): Promise<Array<{ key: string; name: string; size: number; lastModified: string }>> {
    const response = await api.get(`/s3-files/${environmentPrefix}/${subfolder}`);
    return response.data;
  },
};
